// ds-typo-ignore-file — renders a pixel-accurate mockup of the report dashboard (stat cards, findings pills, biomarker rows), not marketing-site DS
"use client"
import { blobMediaUrl } from "@/lib/blob-url"
// ds-typo-ignore-file — report mockup uses sub-10px pill labels that simulate the actual report UI

import { useState, useRef, useEffect } from "react"
import Image from "next/image"
import { m } from "framer-motion"
import { useTranslations } from "@/lib/intl/use-translations"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { useLocalePath } from "@/lib/use-localized-navigation"

import dynamic from "next/dynamic"
import Link from "next/link"
import { cn } from "@/lib/utils"
import { BookingCta } from "@/components/ui/booking-cta"
import { LogoMarquee } from "@/components/ui/logo-marquee"
import { reportKeys as k } from "@/i18n/report-keys"
import { useCountry } from "@/components/layout/country-provider"
import { reportMockupSample } from "@/components/ui/report-mockup-sample"
import {
  ChevronRightIcon,
  CheckIcon,
  StarIcon,
  NutritionIcon,
  SupplementsIcon,
  FollowUpIcon,
  ExerciseIcon,
  ActionPlanIcon,
  BodyFigureIcon,
} from "@/components/ui/icons"
import { BrainOrganIcon } from "@/components/ui/organ-icons"
import { PackageIcon } from "@/components/ui/package-icons"
import { ReportMockupIpad } from "@/components/ui/report-mockup-ipad"
import { FindingsLegend } from "@/components/ui/findings-legend"
import { ReportMockupIphone } from "@/components/ui/report-mockup-iphone"
import { TestimonialsSection } from "../../_components/testimonials-section"
import type { Testimonial } from "@/lib/cms"
import { FaqSection } from "@/components/ui/faq-section"
import { CtaBanner } from "@/components/ui/cta-banner"
import { homeKeys as hk } from "@/i18n/home-keys"

// Lazy-load heavy below-fold components to reduce TBT and exclude from SSR prerender
const ScatteredData = dynamic(
  () =>
    import("@/components/ui/scattered-data").then((m) => ({
      default: m.ScatteredData,
    })),
  { ssr: false }
)
const AutoplayVideo = dynamic(
  () =>
    import("@/components/ui/autoplay-video").then((m) => ({
      default: m.AutoplayVideo,
    })),
  { ssr: false }
)
/* ───────────────────── Page content data ───────────────────── */

/* RECOMMENDATION_CARDS — moved inside component to access translations */

const TRUST_ICONS = [
  {
    icon: <BodyFigureIcon size={22} />,
  },
  {
    // Fingerprint — biometric security, evokes personal/private data protection
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M7.864 4.243A7.5 7.5 0 0 1 19.5 10.5c0 2.92-.556 5.709-1.568 8.268" />
        <path d="M5.742 6.364A7.465 7.465 0 0 0 4.5 10.5a7.464 7.464 0 0 1-1.15 3.993" />
        <path d="M5.339 18.052A11.209 11.209 0 0 0 8.25 10.5a3.75 3.75 0 1 1 7.5 0c0 .527-.021 1.049-.064 1.565" />
        <path d="M12 10.5a14.94 14.94 0 0 1-3.6 9.75" />
        <path d="M15.033 15.654a18.666 18.666 0 0 1-2.485 5.33" />
      </svg>
    ),
  },
  {
    // Paper airplane — "send to your doctor", product-specific sharing
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m22 2-7 20-4-9-9-4 20-7Z" />
        <path d="M22 2 11 13" />
      </svg>
    ),
  },
  {
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 16 16"
        className="shrink-0 overflow-hidden rounded-[2px]"
        aria-hidden="true"
      >
        <rect width="16" height="16" fill="currentColor" />
        <rect x="6.5" y="3" width="3" height="10" rx="0.4" fill="#f4f4f4" />
        <rect x="3" y="6.5" width="10" height="3" rx="0.4" fill="#f4f4f4" />
      </svg>
    ),
  },
]

/* TESTIMONIALS — moved inside component to access translations */

/* ───────────────────── Helpers ───────────────────── */

function CountUp({
  target,
  duration = 1500,
  className = "",
  suffix = "",
}: {
  target: number
  duration?: number
  className?: string
  suffix?: string
}) {
  const [count, setCount] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const hasAnimated = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true
          const start = performance.now()
          const animate = (now: number) => {
            const progress = Math.min((now - start) / duration, 1)
            const eased = 1 - Math.pow(1 - progress, 3) // ease-out cubic
            setCount(Math.round(eased * target))
            if (progress < 1) requestAnimationFrame(animate)
          }
          requestAnimationFrame(animate)
        }
      },
      { threshold: 0.3 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [target, duration])

  return (
    <span ref={ref} className={className}>
      {count}
      {suffix}
    </span>
  )
}

function GoogleStars() {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {Array.from({ length: 5 }, (_, i) => (
        <StarIcon key={i} />
      ))}
      <svg width="18" height="18" viewBox="0 0 48 48" className="ml-0.5">
        <path
          fill="#FFC107"
          d="M43.6 20.1H42V20H24v8h11.3C33.9 33.5 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.2-2.7-.4-3.9z"
        />
        <path
          fill="#FF3D00"
          d="M6.3 14.7l6.6 4.8C14.4 15.2 18.8 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
        />
        <path
          fill="#4CAF50"
          d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.4 0-9.9-3.5-11.3-8.5l-6.5 5C9.5 39.6 16.2 44 24 44z"
        />
        <path
          fill="#1976D2"
          d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.2-2.7-.4-3.9z"
        />
      </svg>
    </div>
  )
}

/* ───────────────────── Clear Signals gauge ───────────────────── */

function ClearSignalsGauge() {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [drawn, setDrawn] = useState(false)

  useEffect(() => {
    const node = wrapperRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setDrawn(true)
          observer.disconnect()
        }
      },
      { threshold: 0.3 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  // Arc path: 270° arc from native 3 o'clock → 12 o'clock (clockwise).
  // With the SVG rotated 135°, native 3 o'clock lands at 7:30 (bottom-left
  // endpoint) and native 12 o'clock at 4:30 (bottom-right endpoint), so
  // the stroke reveal traces left → top → right as requested.
  const arcPath = "M 110 60 A 50 50 0 1 1 60 10"

  return (
    <div
      ref={wrapperRef}
      className="relative flex h-48 w-48 items-center justify-center"
    >
      <svg
        viewBox="0 0 120 120"
        className="h-full w-full"
        style={{ transform: "rotate(135deg)" }}
      >
        <path
          d={arcPath}
          fill="none"
          stroke="#f0f0f0"
          strokeWidth="10"
          strokeLinecap="round"
        />
        <path
          d={arcPath}
          pathLength={100}
          fill="none"
          stroke="#A4B55E"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray="100"
          strokeDashoffset={drawn ? 0 : 100}
          style={{
            transition: "stroke-dashoffset 1.2s cubic-bezier(0, 0, 0.2, 1)",
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="type-display-sm text-neutral-900">4.8</span>
        <span className="mt-0.5 text-xs tracking-wider text-neutral-400">
          mmol/L
        </span>
      </div>
    </div>
  )
}

/* ───────────────────── Logo marquee ───────────────────── */

type LogoTagKey = "partnerWith" | "experienceAt" | "asSeenOn"

const LOGO_ITEMS: {
  src: string
  alt: string
  h: number
  tagKey: LogoTagKey
  href?: string
}[] = [
  {
    src: blobMediaUrl("/images/logos/unilabs.svg"),
    alt: "Unilabs",
    h: 20,
    tagKey: "partnerWith",
  },
  {
    src: blobMediaUrl("/images/logos/octorad.png"),
    alt: "Octorad",
    h: 22,
    tagKey: "partnerWith",
  },
  {
    src: blobMediaUrl("/images/logos/oxford.svg"),
    alt: "University of Oxford",
    h: 36,
    tagKey: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/google.svg"),
    alt: "Google",
    h: 32,
    tagKey: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/quote.svg"),
    alt: "Quote",
    h: 28,
    tagKey: "asSeenOn",
    href: "https://www.quotenet.nl/zakelijk/a69976033/cody-gakpo-en-anderen-storten-6-miljoen-in-pre-scan-voor-millennials/",
  },
  {
    src: blobMediaUrl("/images/logos/siemens.svg"),
    alt: "Siemens",
    h: 28,
    tagKey: "partnerWith",
  },
  {
    src: blobMediaUrl("/images/logos/techeu.svg"),
    alt: "Tech.eu",
    h: 22,
    tagKey: "asSeenOn",
    href: "https://tech.eu/2026/01/13/ahead-health-raises-6m-to-build-ai-powered-health-system-in-europe/",
  },
  {
    src: blobMediaUrl("/images/logos/park.png"),
    alt: "Park Igls",
    h: 48,
    tagKey: "partnerWith",
  },
  {
    src: blobMediaUrl("/images/logos/kaia.svg"),
    alt: "Kaia Health",
    h: 40,
    tagKey: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/longevity-center.svg"),
    alt: "Longevity Center",
    h: 36,
    tagKey: "partnerWith",
  },
  {
    src: blobMediaUrl("/images/logos/tum.svg"),
    alt: "TU Munich",
    h: 32,
    tagKey: "experienceAt",
  },
  {
    src: blobMediaUrl("/images/logos/sifted.png"),
    alt: "Sifted",
    h: 28,
    tagKey: "asSeenOn",
    href: "https://sifted.eu/articles/ahead-health-6m-seed",
  },
  {
    src: blobMediaUrl("/images/logos/newzapiens.svg"),
    alt: "Newzapiens",
    h: 22,
    tagKey: "partnerWith",
  },
]

/**
 * The sample follow-up booking's partner site. CH shows the Zurich radiology
 * partner; DE, which sells blood tests only, shows its Berlin blood-draw
 * partner (CMS `locations` #49, Berlin Mitte).
 */
const FOLLOW_UP_CLINIC = {
  ch: {
    name: "Radiologie Hirslanden Zürich",
    address: "Witellikerstrasse 40, 8032 Zürich",
    image: blobMediaUrl("/images/report/hirslanden-zurich.png"),
  },
  de: {
    name: "Berlin Mitte",
    address: "Schicklerstraße 5, 10179 Berlin",
    image:
      "https://rc1mdjsplvzsekgk.public.blob.vercel-storage.com/Berlin_eterno_card.avif",
  },
  // AT has no partner clinic yet: the city only, with the Ahead mark in
  // place of a clinic photo.
  at: {
    name: "Wien",
    address: "Österreich",
    image: "/icons/ahead-logo.svg",
  },
} as const

/* ───────────────────── Page component ───────────────────── */

export default function ReportPage({
  initialFaqs,
  initialTestimonials = [],
}: {
  initialFaqs: { question: string; answer: string }[]
  initialTestimonials?: Testimonial[]
}) {
  const t = useTranslations("report")
  const ht = useTranslations("home")
  const sample = reportMockupSample(t(k.mockupBiomarkerTotal))
  // Germany sells blood tests only: every MRI surface on this page (the scan
  // pill, the radiology card, the brain/spine sections) drops out, and the
  // sample follow-up booking points at a German blood-draw partner.
  const bloodOnly = useCountry().code === "de"
  // AT has no partner clinic yet, so the sample booking names the city only.
  // country-gate-ignore: AT content shape, not a page gate
  const isAt = useCountry().code === "at"
  const followUpClinic = FOLLOW_UP_CLINIC[bloodOnly ? "de" : isAt ? "at" : "ch"]
  const lp = useLocalePath()

  const carouselRef = useRef<HTMLDivElement>(null)

  const RECOMMENDATION_CARDS = [
    {
      person: "Tom, 32",
      role: t(k.recTomRole),
      photo: blobMediaUrl("/images/report/person-tom.jpg"),
      thumb: blobMediaUrl("/images/report/recs/omega-3.jpg"),
      title: t(k.recTomTitle),
      category: t(k.recTomCategory),
      categoryIcon: SupplementsIcon,
      frequency: t(k.recTomFrequency),
      impacts: 2,
    },
    {
      person: "David, 45",
      role: t(k.recDavidRole),
      photo: blobMediaUrl("/images/report/person-david.jpg"),
      thumb: blobMediaUrl("/images/report/recs/followup-blood-test.jpg"),
      title: t(k.recDavidTitle),
      category: t(k.recDavidCategory),
      categoryIcon: FollowUpIcon,
      frequency: t(k.recDavidFrequency),
      impacts: 4,
    },
    {
      person: "Claudia, 71",
      role: t(k.recClaudiaRole),
      photo: blobMediaUrl("/images/report/person-claudia.jpg"),
      thumb: blobMediaUrl("/images/report/vitamin-d.jpg"),
      title: t(k.recClaudiaTitle),
      category: t(k.recClaudiaCategory),
      categoryIcon: SupplementsIcon,
      frequency: t(k.recClaudiaFrequency),
      impacts: 1,
    },
    {
      person: "Eva, 38",
      role: t(k.recEvaRole),
      photo: blobMediaUrl("/images/report/person-eva.jpg"),
      thumb: blobMediaUrl("/images/report/recs/glycemic-load.jpg"),
      title: t(k.recEvaTitle),
      category: t(k.recEvaCategory),
      categoryIcon: NutritionIcon,
      frequency: t(k.recEvaFrequency),
      impacts: 2,
    },
    {
      person: "Lena, 48",
      role: t(k.recLenaRole),
      photo: blobMediaUrl("/images/report/person-lena.jpg"),
      thumb: blobMediaUrl("/images/report/followup-optimization.jpg"),
      title: t(k.recLenaTitle),
      category: t(k.recLenaCategory),
      categoryIcon: ExerciseIcon,
      frequency: t(k.recLenaFrequency),
      impacts: 5,
    },
  ]

  return (
    <>
      {/* ─── 1. Hero ─── */}
      <section className="pt-12 pb-4 text-center md:pt-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <a
              href="https://www.google.com/maps/place/Ahead+Health/@47.3734617,8.5311033,17z/data=!4m16!1m9!3m8!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!2sAhead+Health!8m2!3d47.3734618!4d8.5359742!9m1!1b1!16s%2Fg%2F11x1bl_tpp!3m5!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!8m2!3d47.3734618!4d8.5359742!16s%2Fg%2F11x1bl_tpp?entry=ttu"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-full transition-opacity hover:opacity-80"
              aria-label="Read Ahead Health reviews on Google"
            >
              <GoogleStars />
            </a>
          </div>
          <p className="type-label-lg mt-4 text-neutral-400">
            {t(k.heroLabel)}
          </p>
          <h1 className="type-display-lg mx-auto mt-4 max-w-3xl text-center text-balance text-neutral-900">
            {t(k.heading)}
          </h1>
          <p className="type-body-lg mx-auto mt-4 max-w-xl text-pretty text-neutral-600">
            {t(k.subtitle)}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-4">
            <BookingCta
              trackingLabel="Book check-up"
              trackingLocation="report-hero"
              className="w-full sm:w-auto"
            >
              <Button className="group bg-aubergine-800 hover:bg-aubergine-900 relative w-full overflow-hidden text-white sm:w-auto">
                <span className="inline-block transition-transform duration-200 ease-out group-hover:-translate-x-2">
                  {t(k.bookCheckup)}
                </span>
                <span className="absolute right-4 translate-x-3 opacity-0 transition-all duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100">
                  <ArrowRightIcon size={18} />
                </span>
              </Button>
            </BookingCta>
            <Link href={lp("/contact")} className="w-full sm:w-auto">
              <Button variant="outline" className="w-full sm:w-auto">
                {t(k.contactUs)}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── 1a. iPad report mockup ─── */}
      <section className="pt-10 pb-8 md:pt-16 md:pb-12">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <m.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.45 }}
          >
            {/* iPhone frame on mobile, iPad on md+ */}
            <div className="md:hidden">
              <ReportMockupIphone />
              <div className="mt-5 flex justify-center">
                <BookingCta
                  trackingLabel="Book check-up"
                  trackingLocation="report-mockup"
                  className="type-body-md text-aubergine-800 inline-flex items-center gap-1 font-medium underline-offset-4 hover:underline"
                >
                  {t(k.bookCheckup)}
                  <ArrowRightIcon size={14} />
                </BookingCta>
              </div>
            </div>
            <div className="hidden md:block">
              <ReportMockupIpad
                explainers={{
                  selector: t("explainerSelector"),
                  nav: t("explainerNav"),
                  bioAge: t("explainerBioAge"),
                  heartRisk: t("explainerHeartRisk"),
                  biomarkers: t("explainerBiomarkers"),
                  findings: t("explainerFindings"),
                  summary: t("explainerSummary"),
                  sidebarSummary: t("explainerSidebarSummary"),
                }}
              />
              <FindingsLegend className="mt-4 justify-center" />
            </div>
          </m.div>
        </div>
      </section>

      {/* ─── Logo marquee ─── */}
      <section className="py-2 md:py-4">
        <LogoMarquee
          items={LOGO_ITEMS.map((l) => ({ ...l, label: ht(l.tagKey) }))}
          theme="light"
        />
      </section>

      {/* ─── 1b. Turn scattered medical data ─── */}
      <section className="py-10 md:py-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <ScatteredData
              heading={t(k.scatteredHeading)}
              headingHighlight=""
              subtitle={t(k.scatteredSubtitle)}
              pillLabels={{
                medicalRecord: t(k.pillMedicalRecord),
                mriScan: bloodOnly ? undefined : t(k.pillMriScan),
                prescriptions: t(k.pillPrescriptions),
                bloodWork: t(k.pillBloodWork),
                actionPlan: t(k.pillActionPlan),
                lifestyleGoals: t(k.pillLifestyleGoals),
              }}
              cardName={t(k.scatteredCardName)}
              cardBiomarkers={t(k.scatteredCardBiomarkers)}
              cardActionPoints={t(k.scatteredCardActionPoints)}
            />
          </div>
        </div>
      </section>

      {/* ─── 2-3. "A report that doesn't feel like homework" ─── */}
      <section className="pt-14 md:pt-32">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-6 px-4 md:px-10">
          {/* Section heading row */}
          <div>
            <div className="flex flex-col gap-6 px-6 md:flex-row md:items-end md:justify-between md:px-8">
              <div className="max-w-lg">
                <h2 className="type-display-sm leading-tight text-neutral-900">
                  {t(k.sectionHeading)}
                </h2>
                <p className="type-body-lg mt-3 text-neutral-600">
                  {t(k.sectionSubtitle)}
                </p>
              </div>
              <BookingCta
                trackingLabel="Book check-up"
                trackingLocation="report"
                className="w-full shrink-0 md:w-auto"
              >
                <Button className="bg-aubergine-800 hover:bg-aubergine-900 w-full text-white md:w-auto">
                  {t(k.bookCheckup)}
                </Button>
              </BookingCta>
            </div>
          </div>

          {/* Stats card — grey rounded container */}
          <div>
            <div className="flex flex-col gap-8 rounded-[24px] bg-neutral-100 p-6 md:gap-10 md:p-8">
              {/* Sub-heading */}
              <div>
                <h3 className="type-title-lg text-neutral-900">
                  {t(k.seeHealthData)}
                </h3>
                <p className="mt-1 text-sm text-neutral-600 md:text-base">
                  {t(k.everyDatapoint)}
                </p>
              </div>

              {/* 4 stat cards row */}
              <div
                className={cn(
                  "grid grid-cols-1 gap-4 md:gap-5",
                  bloodOnly ? "md:grid-cols-3" : "md:grid-cols-4"
                )}
              >
                {/* Card 1: Biological age — same bg as iPad sidebar bioAge card */}
                <div className="relative flex flex-col justify-between overflow-hidden rounded-[18px] p-4">
                  <Image
                    src={blobMediaUrl(
                      "/images/report/backgrounds/summary_bg_1.png"
                    )}
                    alt=""
                    fill
                    className="z-0 object-cover object-center"
                    aria-hidden="true"
                  />
                  <div className="relative z-10">
                    <CountUp
                      target={24}
                      className="type-display-sm leading-none font-medium text-white"
                    />
                    <p className="mt-3 text-sm text-white md:text-base">
                      {t(k.bioAgeLabel)}
                    </p>
                    {/* Progress bar with pill */}
                    <div className="relative mt-2 h-2 w-full overflow-visible rounded-full bg-white/30">
                      {/* Diagonal stripe segment */}
                      <div
                        className="absolute top-0 bottom-0 rounded-full"
                        style={{
                          left: "25%",
                          right: "35%",
                          background:
                            "repeating-linear-gradient(45deg, rgba(255,255,255,0.5), rgba(255,255,255,0.5) 2px, transparent 2px, transparent 5px)",
                        }}
                      />
                      {/* Marker line */}
                      <div
                        className="absolute top-[-2px] bottom-[-2px] w-[2px] rounded-full bg-white"
                        style={{ left: "65%" }}
                      />
                      {/* Pill with "↓ 11 yrs" */}
                      <div
                        className="absolute top-1/2 flex -translate-y-1/2 items-center gap-0.5 rounded-full bg-white px-1.5 py-0.5 shadow-sm"
                        style={{ left: "-2px" }}
                      >
                        <svg
                          width="8"
                          height="8"
                          viewBox="0 0 10 10"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M5 1v8M2 6l3 3 3-3" />
                        </svg>
                        <span className="text-[10px] font-medium text-neutral-900">
                          {/* ds-typo-ignore */}
                          11 {t(k.yrsLabel)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="relative z-10 mt-4 text-xs leading-snug text-white md:text-sm">
                    {t(k.bioAgeDesc)}
                  </p>
                </div>

                {/* Card 2: Biomarkers — segmented bar + pills */}
                <div className="flex flex-col justify-between rounded-[18px] bg-white p-4">
                  <div>
                    <CountUp
                      target={sample.total}
                      className="type-display-sm leading-none font-medium text-neutral-900"
                    />
                    <p className="mt-2 text-sm text-neutral-800 md:text-base">
                      {t(k.biomarkersLabelShort)}
                    </p>
                  </div>
                  <div className="mt-4">
                    {/* Segmented bar */}
                    <div className="flex h-1.5 items-stretch gap-1">
                      <div
                        className="bg-deep-green-500 rounded-full"
                        style={{
                          width: `${(sample.optimal / sample.total) * 100}%`,
                        }}
                      />
                      <div
                        className="bg-bright-green-500 rounded-full"
                        style={{
                          width: `${(sample.inRange / sample.total) * 100}%`,
                        }}
                      />
                      <div
                        className="bg-terracotta-300 rounded-full"
                        style={{
                          width: `${(sample.outOfRange / sample.total) * 100}%`,
                        }}
                      />
                    </div>
                    {/* Pills */}
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      <span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-[10px] text-neutral-800">
                        {/* ds-typo-ignore */}
                        <span className="font-medium">{sample.optimal}</span>
                        <span className="text-neutral-600">
                          {t(k.optimalLower)}
                        </span>
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-[10px] text-neutral-800">
                        {/* ds-typo-ignore */}
                        <span className="font-medium">{sample.inRange}</span>
                        <span className="text-neutral-600">
                          {t(k.inRangeLower)}
                        </span>
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-[10px] text-neutral-800">
                        {/* ds-typo-ignore */}
                        <span className="font-medium">{sample.outOfRange}</span>
                        <span className="text-neutral-600">
                          {t(k.outOfRangeLower)}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card 3: Findings — dot pills */}
                <div className="flex flex-col justify-between rounded-[18px] bg-white p-4">
                  <div>
                    <CountUp
                      target={10}
                      className="type-display-sm leading-none font-medium text-neutral-900"
                    />
                    <p className="mt-2 text-sm text-neutral-800 md:text-base">
                      {t(k.findingsLabel)}
                    </p>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-[10px] text-neutral-800">
                      {/* ds-typo-ignore */}
                      <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" />
                      <span className="font-medium">1</span>
                      <span className="text-neutral-600">
                        {t(k.noFindingSingular)}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-[10px] text-neutral-800">
                      {/* ds-typo-ignore */}
                      <span className="bg-deep-green-500 h-1.5 w-1.5 rounded-full" />
                      <span className="font-medium">5</span>
                      <span className="text-neutral-600">
                        {t(k.observation)}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-[10px] text-neutral-800">
                      {/* ds-typo-ignore */}
                      <span className="bg-bright-green-500 h-1.5 w-1.5 rounded-full" />
                      <span className="font-medium">4</span>
                      <span className="text-neutral-600">{t(k.minor)}</span>
                    </span>
                  </div>
                </div>

                {/* Card 4: Radiology findings — stacked MRI panels. Not on
                    blood-only markets, which sell no scan. */}
                {!bloodOnly && (
                  <div className="flex flex-col items-center justify-between rounded-[18px] bg-white p-4">
                    <span className="self-start text-sm text-neutral-800 md:text-base">
                      {t(k.radiologyFindingsLabel)}
                    </span>
                    {/* Stacked dark panels — back panels animate out when scrolled into view */}
                    <div className="relative my-3 h-16 w-16">
                      <m.div
                        initial={{ rotate: 0, x: 0, y: 0 }}
                        whileInView={{ rotate: -10, x: -10, y: 3 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{
                          duration: 0.9,
                          delay: 0.25,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                        className="absolute inset-0 rounded-2xl border border-white bg-neutral-900 shadow-md"
                      />
                      <m.div
                        initial={{ rotate: 0, x: 0, y: 0 }}
                        whileInView={{ rotate: 10, x: 10, y: 3 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{
                          duration: 0.9,
                          delay: 0.35,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                        className="absolute inset-0 rounded-2xl border border-white bg-neutral-900 shadow-md"
                      />
                      <div className="absolute inset-0 overflow-hidden rounded-2xl border border-white bg-neutral-900 shadow-md">
                        <Image
                          src={blobMediaUrl("/images/report/brain-scan.png")}
                          alt=""
                          fill
                          sizes="64px"
                          className="object-cover"
                        />
                      </div>
                    </div>
                    <p className="text-sm font-medium text-neutral-900">
                      <CountUp target={10} className="font-medium" />{" "}
                      {t(k.findingsSuffix)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 3 feature cards — V layout on tablet (2 top + 1 centered), 3 columns on desktop */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Personalized health scores — matches Framer DOM exactly */}
            <div className="md:order-3 md:col-span-2 md:mx-auto md:max-w-[calc(50%-12px)] lg:order-none lg:col-span-1 lg:mx-0 lg:max-w-none">
              <div className="flex h-full flex-col overflow-hidden rounded-[24px] bg-white px-8 pt-8 pb-0 md:h-[420px]">
                <h3 className="type-title-lg text-neutral-900">
                  {t(k.personalizedScoresTitle)}
                </h3>
                <p className="type-body-md mt-2 text-neutral-500">
                  {t(k.personalizedScoresDesc)}
                </p>
                {/* Image area — negative margins to break out of px-8 padding, bleeds to all edges */}
                <div className="relative -mx-8 mt-auto min-h-[260px] flex-1 pt-6 md:min-h-[320px]">
                  {/* 63 years — heart icon, behind the figure (rendered before Image so the
                      transparent PNG cutout of the woman visually overlaps the pill's right edge) */}
                  <div
                    className="absolute bottom-[10%] left-[calc(2%+4px)] flex items-center gap-1.5 rounded-[53px] px-3 py-2"
                    style={{ backgroundColor: "#EBF599" }}
                  >
                    <svg width="16" height="16" viewBox="0 0 17 16" fill="none">
                      <path
                        d="M8.5 14s-6-4.35-6-8.15A3.35 3.35 0 015.85 2.5c1.27 0 2.05.8 2.65 1.5.6-.7 1.38-1.5 2.65-1.5A3.35 3.35 0 0114.5 5.85C14.5 9.65 8.5 14 8.5 14z"
                        stroke="#5a5a3a"
                        strokeWidth="1.2"
                        fill="none"
                      />
                    </svg>
                    <span className="type-body-md text-neutral-900">
                      {t(k.ageLabel63)}
                    </span>
                  </div>
                  <Image
                    src={blobMediaUrl("/images/report/elderly-woman.png")}
                    alt="Personalized health scores"
                    fill
                    priority
                    className="object-cover object-top"
                  />
                  {/* Yellow-green badges with icons — positioned to match Framer */}
                  {/* 56 years — brain icon, top-right */}
                  <div
                    className="absolute top-[16%] right-[6%] flex items-center gap-1.5 rounded-[53px] px-3 py-2"
                    style={{ backgroundColor: "#EBF599" }}
                  >
                    <BrainOrganIcon size={16} className="text-neutral-900" />
                    <span className="type-body-md text-neutral-900">
                      {t(k.ageLabel56)}
                    </span>
                  </div>
                  {/* 67 years — chart icon, bottom-right */}
                  <div
                    className="absolute right-[5%] bottom-[8%] flex items-center gap-1.5 rounded-[53px] px-3 py-2"
                    style={{ backgroundColor: "#EBF599" }}
                  >
                    <svg width="16" height="10" viewBox="0 0 17 10" fill="none">
                      <path
                        d="M1 9l4-4 3 2 8-6"
                        stroke="#5a5a3a"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                    <span className="type-body-md text-neutral-900">
                      {t(k.ageLabel67)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Clear action items — purple card */}
            <div className="md:order-2 lg:order-none">
              <div
                className="flex h-full flex-col overflow-hidden rounded-[24px] px-6 pt-6 pb-0 md:h-[420px] md:px-8 md:pt-8"
                style={{ backgroundColor: "#644F8B" }}
              >
                <h3 className="type-title-lg text-white">
                  {t(k.clearActionItemsTitle)}
                </h3>
                <p className="mt-2 text-sm text-white/70">
                  {t(k.clearActionItemsDesc)}
                </p>
                {/* Action cards — light surface bleeding to bottom, stacked detailed cards */}
                <div className="bg-aubergine-50 mt-4 flex flex-1 flex-col gap-3 rounded-t-[16px] p-3">
                  {[
                    {
                      title: t(k.actionDietTitle),
                      desc: t(k.actionDietDesc),
                      image: blobMediaUrl(
                        "/images/report/anti-inflammatory-diet.jpg"
                      ),
                      pills: [
                        { label: t(k.pillNutrition), kind: "leaf" as const },
                        { label: t(k.pillDaily), kind: "clock" as const },
                        {
                          label: t(k.pillThreeImpactAreas),
                          kind: "target" as const,
                        },
                      ],
                      biomarkersLabel: t(k.threeRelevantBiomarkers),
                    },
                    {
                      title: t(k.actionVitaminDTitle),
                      desc: t(k.actionVitaminDDesc),
                      image: blobMediaUrl("/images/report/vitamin-d.jpg"),
                      pills: [
                        { label: t(k.pillSupplements), kind: "pill" as const },
                        { label: t(k.pillDaily), kind: "clock" as const },
                        {
                          label: t(k.pillOneImpactArea),
                          kind: "target" as const,
                        },
                      ],
                      biomarkersLabel: t(k.oneRelevantBiomarker),
                    },
                  ].map((item, i) => (
                    <article
                      key={i}
                      className="flex flex-col gap-4 rounded-lg bg-white p-4"
                      aria-label={item.title}
                    >
                      <div className="flex items-center gap-3 text-left">
                        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-2xl shadow">
                          <Image
                            src={item.image}
                            alt=""
                            fill
                            sizes="64px"
                            className="object-cover"
                            aria-hidden="true"
                          />
                        </div>
                        <div className="flex grow items-center justify-between">
                          <div className="flex min-w-0 flex-col gap-1">
                            <span className="">{item.title}</span>
                            <span className="type-body-sm text-neutral-500">
                              {item.desc}
                            </span>
                          </div>
                          <div className="shrink-0" aria-hidden="true">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-chevron-right size-4"
                            >
                              <path d="m9 18 6-6-6-6" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {item.pills.map((pill, pi) => (
                          <div
                            key={pi}
                            className="relative inline-flex items-center justify-center gap-1.5 rounded-[40px] border border-zinc-800/10 bg-transparent px-[8px] pt-1 pr-2.5 pb-1 pl-1.5 text-xs font-medium text-zinc-800"
                          >
                            {pill.kind === "leaf" && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-leaf h-4 w-4"
                              >
                                <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
                                <path d="M2 21c0-3 1.85-5.36 5.08-6" />
                              </svg>
                            )}
                            {pill.kind === "pill" && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-pill h-4 w-4"
                              >
                                <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
                                <path d="m8.5 8.5 7 7" />
                              </svg>
                            )}
                            {pill.kind === "clock" && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-clock h-4 w-4"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                              </svg>
                            )}
                            {pill.kind === "target" && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-target h-4 w-4"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <circle cx="12" cy="12" r="6" />
                                <circle cx="12" cy="12" r="2" />
                              </svg>
                            )}
                            <span className="leading-none">{pill.label}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex w-full flex-col gap-2">
                        <div className="flex w-full flex-col gap-2 rounded-xl bg-zinc-100">
                          <div className="w-full px-4 pt-2.5 pb-3">
                            <div className="flex w-full items-center justify-between">
                              <span className="text-xs leading-none md:text-sm">
                                {item.biomarkersLabel}
                              </span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-chevron-down h-3 w-3"
                                aria-hidden="true"
                              >
                                <path d="m6 9 6 6 6-6" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>

            {/* Dynamic journey tracking — HbA1c chart */}
            <div className="md:order-1 lg:order-none">
              <div className="flex h-full flex-col overflow-hidden rounded-[24px] bg-white p-6 md:h-[420px] md:p-8">
                <h3 className="type-title-lg text-neutral-900">
                  {t(k.dynamicTrackingTitle)}
                </h3>
                <p className="type-body-md mt-2 text-neutral-500">
                  {t(k.dynamicTrackingDesc)}
                </p>
                {/* HbA1c chart — replicates "Hide details" reference layout */}
                <div className="mt-6 flex flex-1 flex-col">
                  {/* Header: NGSP label + Hide details chevron */}
                  <div className="flex items-start justify-between">
                    <span className="type-body-sm text-neutral-700">
                      {t(k.bioHbA1cNGSP)}
                    </span>
                    <span className="type-body-sm flex items-center gap-1 text-neutral-700">
                      {t(k.hideDetails)}
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m18 15-6-6-6 6" />
                      </svg>
                    </span>
                  </div>
                  {/* Big value 5.9 % */}
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="type-display-sm text-neutral-900">
                      5.9
                    </span>
                    <span className="text-sm text-neutral-700">%</span>
                  </div>
                  {/* Intermediate pill */}
                  <span className="type-body-sm bg-terracotta-100 text-terracotta-400 mt-2 w-fit rounded-full px-3 py-1 font-medium">
                    {t(k.statusIntermediate)}
                  </span>
                  {/* Chart plot area */}
                  <div className="relative mt-5 min-h-[120px] flex-1">
                    {/* Left column: zone labels, evenly spaced to match 3 rows */}
                    <div className="absolute top-0 bottom-0 left-0 flex w-[140px] flex-col justify-between text-sm text-neutral-800">
                      <span className="flex h-6 items-center">
                        {t(k.zoneDiabetes)}
                      </span>
                      <span className="flex h-6 items-center">
                        {t(k.zonePreDiabetes)}
                      </span>
                      <span className="flex h-6 items-center">
                        {t(k.zoneNormal)}
                      </span>
                    </div>
                    {/* Plot region (right of labels, left of color bar) */}
                    <div className="absolute top-0 right-3 bottom-0 left-[144px]">
                      {/* Pre-diabetes gradient band (middle 33% of height) */}
                      <m.div
                        initial={{ opacity: 0, scaleX: 0 }}
                        whileInView={{ opacity: 1, scaleX: 1 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{
                          duration: 1.1,
                          ease: [0.16, 1, 0.3, 1],
                          delay: 0.1,
                        }}
                        className="absolute right-0 left-0 origin-left rounded bg-gradient-to-r from-[#fdf1e9] via-[#fbe0d0] to-[#f5c5af]" // ds-color-ignore: gradient stop
                        style={{ top: "33%", bottom: "33%" }}
                      />
                      {/* Vertical dashed line near the right edge */}
                      <m.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        className="absolute top-0 bottom-0 border-l border-dashed border-neutral-300"
                        style={{ left: "87%" }}
                      />
                      {/* Data dots: Jan 4 / Apr 6 / Jun 8 / Sep 4 */}
                      {[
                        { x: 12, y: 48, delay: 0.5 },
                        { x: 37, y: 55, delay: 0.65 },
                        { x: 62, y: 66, delay: 0.8 },
                        { x: 87, y: 52, delay: 0.95 },
                      ].map((dot, i) => (
                        <m.div
                          key={i}
                          initial={{ opacity: 0, scale: 0 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true, margin: "-40px" }}
                          transition={{
                            duration: 0.4,
                            delay: dot.delay,
                            ease: [0.16, 1, 0.3, 1],
                          }}
                          className="absolute h-4 w-4"
                          style={{
                            left: `${dot.x}%`,
                            top: `${dot.y}%`,
                            transform: "translate(-50%, -50%)",
                          }}
                        >
                          <div className="relative h-full w-full rounded-full border border-neutral-200 bg-white shadow-sm">
                            <div className="bg-foreground absolute inset-[3px] rounded-full" />
                          </div>
                        </m.div>
                      ))}
                    </div>
                    {/* Right-side vertical color bar (3 segments) */}
                    <div className="absolute top-0 right-0 bottom-0 flex w-1.5 flex-col gap-1">
                      <m.div
                        initial={{ scaleY: 0 }}
                        whileInView={{ scaleY: 1 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{
                          duration: 0.5,
                          delay: 0.2,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                        className="flex-1 origin-top rounded-full bg-[#2a1d4c]"
                      />
                      <m.div
                        initial={{ scaleY: 0 }}
                        whileInView={{ scaleY: 1 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{
                          duration: 0.5,
                          delay: 0.3,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                        className="bg-terracotta-400 flex-1 origin-top rounded-full"
                      />
                      <m.div
                        initial={{ scaleY: 0 }}
                        whileInView={{ scaleY: 1 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{
                          duration: 0.5,
                          delay: 0.4,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                        className="bg-bright-green-500 flex-1 origin-top rounded-full"
                      />
                    </div>
                  </div>
                  {/* X-axis labels */}
                  <div
                    className="mt-2 flex justify-between text-xs text-neutral-500"
                    style={{ paddingLeft: "144px", paddingRight: "0.75rem" }}
                  >
                    <span>{t(k.chartMonthJan)}</span>
                    <span>{t(k.chartMonthApr)}</span>
                    <span>{t(k.chartMonthJun)}</span>
                    <span>{t(k.chartMonthSep)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 4. "Turn markers into meaning" — biomarker cards ─── */}
      <section className="py-12 md:py-28">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-6 px-4 md:px-10">
          {/* Section heading row */}
          <div>
            <div className="flex flex-col gap-4 px-6 md:flex-row md:items-end md:justify-between md:px-8">
              <div>
                <h2 className="type-display-sm text-neutral-900">
                  {t(k.turnMarkersHeading)}
                </h2>
                <p className="type-body-lg mt-3 max-w-lg text-neutral-600">
                  {t(k.turnMarkersSubtitle)}
                </p>
              </div>
              <BookingCta
                trackingLabel="Book check-up"
                trackingLocation="report-markers"
                className="w-full flex-shrink-0 md:w-auto"
              >
                <Button className="group bg-aubergine-800 hover:bg-aubergine-900 relative w-full overflow-hidden text-white md:w-auto">
                  <span className="inline-block transition-transform duration-200 ease-out group-hover:-translate-x-2">
                    {t(k.bookCheckup)}
                  </span>
                  <span className="absolute right-4 translate-x-3 opacity-0 transition-all duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100">
                    <ArrowRightIcon size={18} />
                  </span>
                </Button>
              </BookingCta>
            </div>
          </div>

          {/* White card: text left + biomarker marquee right */}
          <div>
            <div className="flex flex-col gap-8 rounded-[24px] bg-white p-6 md:flex-row md:p-8">
              {/* Left: sub-heading + description */}
              <div className="flex flex-shrink-0 flex-col md:w-[38%]">
                <h3 className="type-title-lg text-neutral-900">
                  {t(k.marqueeHeading)}
                </h3>
                <p className="type-body-md mt-3 text-neutral-500">
                  {t(k.marqueeSubtitle)}
                </p>
              </div>

              {/* Right: vertical auto-scrolling biomarker marquee */}
              <div
                className="relative overflow-hidden md:w-[62%]"
                style={{
                  height: 420,
                  maskImage:
                    "linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)",
                  WebkitMaskImage:
                    "linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)",
                }}
              >
                <style>{`
                  @keyframes biomarker-scroll {
                    0% { transform: translateY(0); }
                    100% { transform: translateY(-50%); }
                  }
                `}</style>
                <div
                  className="flex flex-col gap-6 px-6 hover:[animation-play-state:paused]"
                  style={{ animation: "biomarker-scroll 50s linear infinite" }}
                >
                  {[...Array.from({ length: 2 })].map((_, setIdx) => (
                    <div key={setIdx} className="flex flex-col gap-3">
                      {[
                        {
                          label: t(k.bioCorticalGrayMatter),
                          value: "34.2",
                          unit: "%",
                          status: t(k.statusNormal),
                          statusBg: "bg-deep-green-500/10",
                          statusText: "text-deep-green-500",
                          segments: [
                            { h: 30, color: "#ed9062" },
                            { h: 40, color: "#b8c664", active: true },
                            { h: 30, color: "#408c49" },
                          ],
                          dotY: 45,
                          ghosts: [50, 42],
                        },
                        {
                          label: t(k.bioHbA1cNGSP),
                          value: "5.9",
                          unit: "%",
                          status: t(k.statusAboveRange),
                          statusBg: "bg-terracotta-300/10",
                          statusText: "text-terracotta-300",
                          segments: [
                            { h: 45, color: "#408c49" },
                            { h: 25, color: "#b8c664" },
                            { h: 30, color: "#ed9062", active: true },
                          ],
                          dotY: 78,
                          ghosts: [60, 70],
                        },
                        {
                          label: t(k.bioFastingGlucose),
                          value: "4.8",
                          unit: "mmol/L",
                          status: t(k.statusNormal),
                          statusBg: "bg-deep-green-500/10",
                          statusText: "text-deep-green-500",
                          segments: [
                            { h: 25, color: "#ed9062" },
                            { h: 50, color: "#408c49", active: true },
                            { h: 25, color: "#ed9062" },
                          ],
                          dotY: 42,
                          ghosts: [38, 45],
                        },
                        {
                          label: t(k.bioVisceralFat),
                          value: "1.6",
                          unit: "L",
                          status: t(k.statusOptimal),
                          statusBg: "bg-deep-green-500/10",
                          statusText: "text-deep-green-500",
                          segments: [
                            { h: 50, color: "#408c49", active: true },
                            { h: 25, color: "#c8b59d" },
                            { h: 25, color: "#ed9062" },
                          ],
                          dotY: 22,
                          ghosts: [28, 25],
                        },
                        {
                          label: t(k.bioIron),
                          value: "17.8",
                          unit: "\u00b5mol/L",
                          status: t(k.statusNormal),
                          statusBg: "bg-deep-green-500/10",
                          statusText: "text-deep-green-500",
                          segments: [
                            { h: 25, color: "#ed9062" },
                            { h: 50, color: "#b8c664", active: true },
                            { h: 25, color: "#ed9062" },
                          ],
                          dotY: 48,
                          ghosts: [52, 55],
                        },
                        {
                          label: t(k.bioLDLCholesterol),
                          value: "2.8",
                          unit: "mmol/L",
                          status: t(k.statusIntermediate),
                          statusBg: "bg-sand-400/15",
                          statusText: "text-[#8b7355]",
                          segments: [
                            { h: 35, color: "#408c49" },
                            { h: 30, color: "#c8b59d", active: true },
                            { h: 35, color: "#ed9062" },
                          ],
                          dotY: 55,
                          ghosts: [48, 52],
                        },
                        {
                          label: t(k.bioVitaminD),
                          value: "68",
                          unit: "nmol/L",
                          status: t(k.statusNormal),
                          statusBg: "bg-deep-green-500/10",
                          statusText: "text-deep-green-500",
                          segments: [
                            { h: 30, color: "#ed9062" },
                            { h: 40, color: "#b8c664", active: true },
                            { h: 30, color: "#c8b59d" },
                          ],
                          dotY: 40,
                          ghosts: [35],
                        },
                        {
                          label: t(k.bioTSH),
                          value: "2.1",
                          unit: "mIU/L",
                          status: t(k.statusNormal),
                          statusBg: "bg-deep-green-500/10",
                          statusText: "text-deep-green-500",
                          segments: [
                            { h: 25, color: "#ed9062" },
                            { h: 50, color: "#408c49", active: true },
                            { h: 25, color: "#ed9062" },
                          ],
                          dotY: 38,
                          ghosts: [42, 40],
                        },
                      ].map((card, i) => (
                        <div
                          key={`${setIdx}-${card.label}`}
                          className={cn(
                            "flex w-full flex-col gap-3 rounded-xl bg-white px-5 py-4 shadow-[0_2px_6px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.08)] md:w-[calc(100%-144px)] md:flex-row md:items-end md:justify-between md:gap-4",
                            i % 2 === 0 ? "md:self-start" : "md:self-end"
                          )}
                        >
                          {/* Title spans full card width on mobile */}
                          <p className="text-sm leading-tight font-normal text-neutral-900 md:hidden">
                            {card.label}
                          </p>
                          <div className="flex items-end justify-between gap-4 md:contents">
                            {/* Left: info */}
                            <div className="flex min-w-0 flex-col">
                              <p className="hidden text-sm leading-tight font-normal text-neutral-900 md:block">
                                {card.label}
                              </p>
                              <div className="flex items-end gap-1.5 md:mt-1.5">
                                <span className="type-headline-lg leading-none font-medium text-neutral-900">
                                  {card.value}
                                </span>
                                <span className="type-body-sm pb-[1px] font-mono tracking-wider text-neutral-900">
                                  {card.unit}
                                </span>
                              </div>
                              <span
                                className={cn(
                                  "type-label-sm mt-2 w-fit rounded-full px-2.5 py-0.5 font-medium",
                                  card.statusBg,
                                  card.statusText
                                )}
                              >
                                {card.status}
                              </span>
                            </div>
                            {/* Right: vertical range bar (matches app) */}
                            <div
                              className="relative flex-shrink-0"
                              style={{ width: 143, height: 79 }}
                            >
                              {/* Active segment gradient highlight */}
                              {(() => {
                                let yOffset = 0
                                return card.segments.map((seg, si) => {
                                  const y = yOffset
                                  yOffset += (seg.h / 100) * 79 + 1
                                  return seg.active ? (
                                    <div
                                      key={si}
                                      className="absolute right-0 left-0 rounded"
                                      style={{
                                        top: y,
                                        height: (seg.h / 100) * 79,
                                        background: `linear-gradient(to right, transparent, ${seg.color}15)`,
                                      }}
                                    />
                                  ) : null
                                })
                              })()}
                              {/* Vertical colored bar segments */}
                              <svg
                                className="absolute overflow-visible"
                                style={{ right: 2, top: 0 }}
                                width={5}
                                height={79}
                              >
                                {(() => {
                                  let yOffset = 0
                                  return card.segments.map((seg, si) => {
                                    const y = yOffset
                                    const h = (seg.h / 100) * 79
                                    yOffset += h + 1
                                    return (
                                      <rect
                                        key={si}
                                        x={0}
                                        y={y}
                                        width={5}
                                        height={h}
                                        rx={2.5}
                                        fill={seg.color}
                                      />
                                    )
                                  })
                                })()}
                              </svg>
                              {/* Dashed vertical line */}
                              <div
                                className="absolute border-l border-dashed border-gray-400 opacity-20"
                                style={{ left: "75%", top: 0, bottom: 0 }}
                              />
                              {/* Ghost dots (historical) */}
                              {card.ghosts.map((gy, gi) => (
                                <div
                                  key={gi}
                                  className="absolute flex items-center justify-center rounded-full bg-gray-900 opacity-40"
                                  style={{
                                    width: 10,
                                    height: 10,
                                    left: `${65 - gi * 12}%`,
                                    top: `${gy}%`,
                                    transform: "translate(-50%, -50%)",
                                  }}
                                >
                                  <div className="h-1 w-1 rounded-full bg-white" />
                                </div>
                              ))}
                              {/* Current value dot */}
                              <div
                                className="absolute flex items-center justify-center rounded-full border-2 border-gray-900 bg-gray-900"
                                style={{
                                  width: 10,
                                  height: 10,
                                  left: "75%",
                                  top: `${card.dotY}%`,
                                  transform: "translate(-50%, -50%)",
                                }}
                              >
                                <div className="h-1 w-1 rounded-full bg-white" />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ─── 5. AI annotation + Side-by-side comparison ─── */}
          {/* MRI-only (brain + spine scans): dropped on blood-only markets. */}
          {!bloodOnly && (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* AI annotation card — brain MRI on terracotta bg */}
              <div>
                <div
                  className="relative h-full min-h-[420px] overflow-hidden rounded-[24px]"
                  style={{ backgroundColor: "var(--color-terracotta-400)" }}
                >
                  {/* Text at top-left */}
                  <div className="relative z-10 p-8 pb-0">
                    <h3 className="type-headline-sm text-white">
                      {t(k.aiAnnotationTitle)}
                    </h3>
                    <p className="mt-2 text-sm text-white/70">
                      {t(k.aiAnnotationDesc)}
                    </p>
                  </div>

                  {/* "One minor finding" badge — top-left area */}
                  <div className="absolute top-[48%] left-6 z-20">
                    <div
                      className="flex items-center gap-1.5 rounded-[53px] px-3 py-2"
                      style={{ backgroundColor: "#EBF599" }}
                    >
                      <div className="bg-light-green-400 mb-[2px] h-1.5 w-1.5 rounded-full" />
                      <span className="type-body-md text-foreground">
                        {t(k.minorFinding)}
                      </span>
                    </div>
                  </div>

                  {/* Looping MRI video — mix-blend-mode: difference on wrapper makes black → orange */}
                  <div className="flex flex-1 items-end justify-center">
                    <div style={{ mixBlendMode: "difference" }}>
                      <AutoplayVideo className="h-[340px] w-[330px] rounded-t-2xl object-cover">
                        <source
                          src={blobMediaUrl(
                            "/images/report/mri-brain-scan.mp4"
                          )}
                          type="video/mp4"
                        />
                      </AutoplayVideo>
                    </div>
                  </div>

                  {/* "2 recommended actions" badge — bottom-right area */}
                  <div className="absolute right-6 bottom-[12%] z-20">
                    <div
                      className="flex items-center gap-1.5 rounded-[53px] px-3 py-2"
                      style={{ backgroundColor: "#EBF599" }}
                    >
                      <ActionPlanIcon size={16} className="text-foreground" />
                      <span className="type-body-md text-foreground">
                        {t(k.recommendedActions)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Side-by-side comparison card — spine MRIs with slide-in */}
              <div>
                <div
                  className="flex h-full min-h-[420px] flex-col overflow-hidden rounded-[24px]"
                  style={{ backgroundColor: "var(--color-foreground)" }}
                >
                  <div className="relative z-10 p-8">
                    <h3 className="type-headline-sm text-white">
                      {t(k.sideBySideTitle)}
                    </h3>
                    <p className="mt-3 text-sm text-white/60">
                      {t(k.sideBySideDesc)}
                    </p>
                  </div>
                  {/* Two rounded image cards side by side with slide-in animation */}
                  <div className="flex flex-1 gap-3 overflow-hidden px-6 pb-6">
                    {/* Left: 2022 — slides in from left */}
                    <m.div
                      initial={{ opacity: 0, x: -40 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.7,
                        ease: [0.16, 1, 0.3, 1],
                        delay: 0.3,
                      }}
                      className="relative min-h-[280px] w-1/2 overflow-hidden rounded-[16px] bg-black"
                    >
                      <Image
                        src={blobMediaUrl(
                          "/images/report/spine-comparison-real.png"
                        )}
                        alt="Spine MRI 2022"
                        fill
                        className="object-contain"
                      />
                      <div className="absolute top-3 left-3 z-10">
                        <div className="rounded-full bg-white/20 px-4 py-1.5 text-sm font-medium text-white backdrop-blur-sm">
                          2022
                        </div>
                      </div>
                      {/* Orange annotation circles */}
                      <div className="border-terracotta-300 absolute top-[40%] left-[55%] h-5 w-5 rounded-full border-2" />
                      <div className="border-terracotta-300 absolute bottom-[18%] left-[50%] h-6 w-6 rounded-full border-2" />
                    </m.div>
                    {/* Right: 2025 — slides in from right */}
                    <m.div
                      initial={{ opacity: 0, x: 40 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.7,
                        ease: [0.16, 1, 0.3, 1],
                        delay: 0.5,
                      }}
                      className="relative min-h-[280px] w-1/2 overflow-hidden rounded-[16px] bg-black"
                    >
                      <Image
                        src={blobMediaUrl(
                          "/images/report/spine-comparison-real.png"
                        )}
                        alt="Spine MRI 2025"
                        fill
                        className="object-contain"
                      />
                      <div className="absolute top-3 right-3 z-10">
                        <div className="rounded-full bg-white/20 px-4 py-1.5 text-sm font-medium text-white backdrop-blur-sm">
                          2025
                        </div>
                      </div>
                      {/* Orange annotation circle */}
                      <div className="border-terracotta-300 absolute bottom-[18%] left-[50%] h-6 w-6 rounded-full border-2" />
                    </m.div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ─── 6. "A plan tailored to your story" — recommendation carousel ─── */}
      <section className="py-10 md:py-20">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-6 px-4 md:px-10">
          {/* Section heading row */}
          <div>
            <div className="flex flex-col gap-4 px-6 md:flex-row md:items-end md:justify-between md:px-8">
              <div>
                <h2 className="type-display-sm text-neutral-900">
                  {t(k.tailoredPlanHeading)}
                </h2>
                <p className="type-body-lg mt-3 max-w-lg text-neutral-600">
                  {t(k.tailoredPlanSubtitle)}
                </p>
              </div>
              <BookingCta
                trackingLabel="Book check-up"
                trackingLocation="report-plan"
                className="w-full flex-shrink-0 md:w-auto"
              >
                <Button className="group bg-aubergine-800 hover:bg-aubergine-900 relative w-full overflow-hidden text-white md:w-auto">
                  <span className="inline-block transition-transform duration-200 ease-out group-hover:-translate-x-2">
                    {t(k.bookCheckup)}
                  </span>
                  <span className="absolute right-4 translate-x-3 opacity-0 transition-all duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100">
                    <ArrowRightIcon size={18} />
                  </span>
                </Button>
              </BookingCta>
            </div>
          </div>

          {/* White recommendation card */}
          <div>
            <div className="overflow-hidden rounded-[24px] bg-white pt-2">
              {/* Auto-scrolling person cards (desktop) + swipable scroll (mobile) */}
              <style>{`
                @keyframes recs-scroll {
                  0% { transform: translateX(0); }
                  100% { transform: translateX(-50%); }
                }
                @media (min-width: 768px) {
                  .recs-reel { animation: recs-scroll 60s linear infinite; width: max-content; }
                }
                @media (max-width: 767px) {
                  .recs-reel:hover { animation-play-state: paused; }
                }
              `}</style>
              <div className="[scrollbar-width:none] overflow-x-auto pb-4 md:overflow-hidden [&::-webkit-scrollbar]:hidden">
                <div
                  ref={carouselRef}
                  className="recs-reel flex [scroll-snap-type:x_mandatory] gap-4 px-6 md:[scroll-snap-type:none] md:gap-6 md:px-0"
                >
                  {/* Duplicate cards for seamless loop */}
                  {[...RECOMMENDATION_CARDS, ...RECOMMENDATION_CARDS].map(
                    (card, idx) => (
                      <div
                        key={`${card.person}-${idx}`}
                        className="flex w-[280px] flex-shrink-0 [scroll-snap-align:start] flex-col self-stretch"
                      >
                        {/* Person photo */}
                        <div className="relative aspect-[3/4] overflow-hidden rounded-[20px]">
                          <Image
                            src={card.photo}
                            alt={card.person}
                            fill
                            sizes="280px"
                            className="object-cover"
                          />
                          {/* Name badge */}
                          <div className="absolute bottom-4 left-3 flex flex-col gap-1.5">
                            <span className="bg-foreground w-fit rounded-full px-4 py-1.5 text-xs font-medium tracking-wider text-white uppercase">
                              {card.person}
                            </span>
                            <span className="bg-foreground w-fit rounded-full px-4 py-1.5 text-xs font-medium tracking-wider text-white uppercase">
                              {card.role}
                            </span>
                          </div>
                        </div>

                        {/* Recommendation card — matches action-card design */}
                        <article className="mt-3 flex w-full flex-1 flex-col gap-3 rounded-2xl bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.06)]">
                          {/* Row 1: thumbnail + title */}
                          <div className="flex items-center gap-3">
                            <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-[#e8e0ee]">
                              <Image
                                src={card.thumb}
                                alt=""
                                fill
                                sizes="48px"
                                className="object-cover"
                                aria-hidden="true"
                              />
                            </div>
                            <h4 className="min-w-0 flex-1 text-base leading-tight font-normal text-neutral-900">
                              {card.title}
                            </h4>
                          </div>
                          {/* Row 2: category + frequency pills */}
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-transparent px-2.5 py-1 text-xs font-medium text-neutral-700">
                              <card.categoryIcon size={14} />
                              {card.category}
                            </span>
                            <span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-transparent px-2.5 py-1 text-xs font-medium text-neutral-700">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                              </svg>
                              {card.frequency}
                            </span>
                          </div>
                          {/* Row 3: biomarkers expandable bar */}
                          <div className="mt-auto flex items-center justify-between rounded-xl bg-neutral-100 px-3 py-2.5 text-xs text-neutral-700">
                            <span>
                              {t(k.impactsBiomarkers, {
                                count: String(card.impacts),
                              })}
                            </span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="m6 9 6 6 6-6" />
                            </svg>
                          </div>
                        </article>
                      </div>
                    )
                  )}
                </div>
              </div>
              <div className="pb-6"></div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 7. Bottom feature cards — matching Framer exactly ─── */}
      <section className="py-10 md:py-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {/* Additional insights — solid blue with white inner card */}
            <div className="order-3">
              <div
                className="flex h-full flex-col rounded-[24px] p-6 text-white md:p-8"
                style={{ backgroundColor: "#7B8EC7" }}
              >
                <h3 className="type-title-lg text-white">
                  {t(k.additionalInsightsTitle)}
                </h3>
                <p className="type-body-md mt-2 mb-4 text-white/80">
                  {t(k.additionalInsightsDesc)}
                </p>
                {/* Inner white card */}
                <div className="mt-6 rounded-2xl bg-white p-5 shadow-[0_8px_24px_rgba(0,0,0,0.08)] md:mt-auto">
                  <div className="flex items-center gap-2.5">
                    <Image
                      src={blobMediaUrl("/images/report/dr-donati.png")}
                      alt={t(k.doctorName)}
                      width={32}
                      height={32}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                    <p className="text-sm font-medium text-neutral-900">
                      {t(k.doctorName)}
                    </p>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-700">
                    {t(k.doctorQuote)}
                  </p>
                  {/* Actions inner row */}
                  <div className="mt-4 flex items-center gap-3 rounded-xl bg-neutral-50 p-2">
                    <Image
                      src={blobMediaUrl(
                        "/images/report/recs/additional-insights.jpg"
                      )}
                      alt=""
                      width={40}
                      height={40}
                      className="h-10 w-10 rounded-xl object-cover"
                    />
                    <span className="flex-1 text-sm text-neutral-700">
                      {t(k.doctorCardActions)}
                    </span>
                    <ChevronRightIcon
                      width={14}
                      height={14}
                      className="text-neutral-400"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Clear signals — white card with 3/4 gauge */}
            <div className="order-2">
              <div className="flex h-full flex-col rounded-[24px] bg-white p-6 md:p-8">
                <h3 className="type-title-lg text-neutral-900">
                  {t(k.clearSignalsTitle)}
                </h3>
                <p className="type-body-md mt-2 text-neutral-500">
                  {t(k.clearSignalsDesc)}
                </p>
                <div className="mt-auto flex flex-col items-center pt-6">
                  <ClearSignalsGauge />
                  <p className="mt-2 text-base font-medium text-neutral-900">
                    {t(k.fastingGlucose)}
                  </p>
                  <span className="bg-light-green-100 mt-3 rounded-full px-3 py-1 text-sm font-medium text-[#7B8C3B]">
                    {t(k.normalRangePill)}
                  </span>
                  <p className="mt-3 text-sm text-neutral-400">
                    {t(k.noActionsNeeded)}
                  </p>
                </div>
              </div>
            </div>

            {/* Your health history — terracotta card with checklist (AHD-3487).
                Same per-card pattern as the lavender/olive cards in this row
                (solid brand surface, white text, white inner panel). */}
            <div className="order-1">
              <div className="bg-terracotta-300 flex h-full flex-col rounded-[24px] p-6 md:p-8">
                <h3 className="type-title-lg text-white">{t(k.intakeTitle)}</h3>
                <p className="type-body-md mt-2 text-white/85">
                  {t(k.intakeDesc)}
                </p>
                {/* Inner white card — captured-history checklist */}
                <ul className="mt-6 flex flex-col gap-1 rounded-2xl bg-white p-2 shadow-[0_8px_24px_rgba(0,0,0,0.08)] md:mt-auto">
                  {[
                    k.intakeItem1,
                    k.intakeItem2,
                    k.intakeItem3,
                    k.intakeItem4,
                  ].map((itemKey) => (
                    <li
                      key={itemKey}
                      className="flex items-center gap-3 rounded-xl p-2.5"
                    >
                      <CheckIcon width={22} height={22} className="shrink-0" />
                      <span className="text-sm font-medium text-neutral-900">
                        {t(itemKey)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Follow-up tests — olive card with white inner card */}
            <div className="order-4">
              <div
                className="flex h-full flex-col rounded-[24px] p-6 md:p-8"
                style={{ backgroundColor: "var(--color-light-green-400)" }}
              >
                <h3 className="type-title-lg text-white">
                  {t(k.followUpTestsTitle)}
                </h3>
                <p className="type-body-md mt-2 text-white/85">
                  {t(k.followUpTestsDesc)}
                </p>
                {/* Inner white card */}
                <div className="mt-6 flex flex-col gap-3 rounded-2xl bg-white p-3 shadow-[0_8px_24px_rgba(0,0,0,0.08)] md:mt-auto">
                  {/* Package row with Ahead Advanced icon */}
                  <div className="flex items-center gap-3 p-2">
                    <PackageIcon variant="blue" size="sm" />
                    <div className="flex-1">
                      <p className="flex items-center gap-1 text-sm font-medium text-neutral-900">
                        {t(k.aheadAdvancedPackage)}
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="text-neutral-400"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
                        </svg>
                      </p>
                      <p className="text-xs text-neutral-500">
                        {t(k.threeAddOns)}
                      </p>
                    </div>
                  </div>
                  {/* Appointment row */}
                  <div className="flex items-center gap-3 rounded-xl bg-neutral-50 p-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#644F8B"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M16 2v4M8 2v4M3 10h18" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-neutral-900">
                        {t(k.mockupApptDate)}
                      </p>
                      <p className="text-xs text-neutral-500">9:00 – 10:00</p>
                    </div>
                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-neutral-700">
                      {t(k.confirmed)}
                    </span>
                  </div>
                  {/* Location row */}
                  <div className="flex items-center gap-3 p-2">
                    <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-md bg-white">
                      <Image
                        src={followUpClinic.image}
                        alt=""
                        fill
                        sizes="40px"
                        className={
                          bloodOnly ? "object-cover" : "object-contain"
                        }
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-neutral-900">
                        {followUpClinic.name}
                      </p>
                      <p className="truncate text-xs text-neutral-500">
                        {followUpClinic.address}
                      </p>
                    </div>
                  </div>
                  {/* Action buttons */}
                  <div className="flex items-center justify-around border-t border-neutral-100 pt-3">
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-xs font-medium text-neutral-700"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 22s-8-7.58-8-13a8 8 0 1 1 16 0c0 5.42-8 13-8 13Z" />
                        <circle cx="12" cy="9" r="3" />
                      </svg>
                      {t(k.getDirections)}
                    </button>
                    <div className="h-4 w-px bg-neutral-200" />
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-xs font-medium text-neutral-700"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      {t(k.addToCalendar)}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 8. Trust signal icons ─── */}
      <section className="py-10 md:py-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
            {TRUST_ICONS.map((item, index) => {
              const trustTitles = [
                t(k.madeForYouTitle),
                t(k.bankSecurityTitle),
                t(k.easySharingTitle),
                t(k.licensedPracticeTitle),
              ]
              const trustDescs = [
                t(k.madeForYouDesc),
                t(k.bankSecurityDesc),
                t(k.easySharingDesc),
                t(k.licensedPracticeDesc),
              ]
              return (
                <div key={index}>
                  <div className="flex flex-col items-start gap-2">
                    <div className="text-neutral-400">{item.icon}</div>
                    <p className="type-title-lg text-neutral-800">
                      {trustTitles[index]}
                    </p>
                    <p className="type-body-lg text-neutral-600">
                      {trustDescs[index]}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ─── 9. Testimonials — shared component (same as homepage + /go/*) ─── */}
      <TestimonialsSection
        heading={t(k.whatOurClientsSay)}
        description={null}
        cmsTestimonials={initialTestimonials}
      />

      {/* ─── 9b. What we're working on ─── */}
      <section className="py-12">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <h2 className="type-title-lg text-center text-neutral-900">
              {t(k.whatWorkingOnHeading)}
            </h2>
          </div>
          <div>
            <div className="mx-auto mt-6 max-w-[520px] overflow-hidden rounded-[16px] border border-neutral-200 bg-white">
              {[
                {
                  src: blobMediaUrl(
                    "/images/report/working-on-lab-reports.png"
                  ),
                  label: t(k.workingOnLabReports),
                },
                {
                  src: blobMediaUrl("/images/report/working-on-wearable.png"),
                  label: t(k.workingOnWearable),
                },
                {
                  src: blobMediaUrl("/images/report/working-on-doctor.png"),
                  label: t(k.workingOnDoctor),
                },
              ].map((item, i, arr) => (
                <div
                  key={i}
                  className={cn(
                    "flex items-center gap-3 p-3",
                    i < arr.length - 1 && "border-b border-neutral-200"
                  )}
                >
                  <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-xl">
                    <Image
                      src={item.src}
                      alt=""
                      fill
                      sizes="40px"
                      className="object-cover"
                      aria-hidden="true"
                    />
                  </div>
                  <p className="type-body-md text-neutral-900">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── 10. FAQ ─── */}
      <FaqSection
        heading={t(k.faqHeading)}
        linkLabel={null}
        items={initialFaqs}
        allowMultiple
        currentPage="report"
      />

      {/* ─── 11. Bottom CTA — shared CtaBanner from homepage ─── */}
      <div>
        <CtaBanner
          title={ht(hk.ctaTitle)}
          subtitle={ht(hk.ctaSubtitle)}
          imageSrc={blobMediaUrl("/images/homepage/cta-bottom.jpeg")}
          primaryCta={{ label: ht(hk.ctaPrimary), href: lp("/pricing") }}
          secondaryCta={{ label: ht(hk.ctaSecondary), href: lp("/contact") }}
        />
      </div>
    </>
  )
}
